import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  noindex?: boolean;
  structuredData?: object;
}

/**
 * SEO Component
 * 
 * Manages meta tags, Open Graph, Twitter Cards, and structured data for each page.
 * Uses react-helmet-async for dynamic meta tag updates.
 * 
 * IMPORTANT: Automatically blocks search engine indexing on non-production environments
 * (localhost, development, staging, preview deployments) to prevent duplicate content issues.
 */
const SEO: React.FC<SEOProps> = ({
  title = 'Level Up Teaching - AI-Powered Lesson Planning for Teachers',
  description = 'Create perfect lesson plans in minutes with AI. Generate standards-aligned content, differentiated instruction materials, and professional assessments for your classroom.',
  keywords = 'AI lesson planning, teacher tools, educational technology, lesson plan generator, standards-aligned content, TEKS, Common Core, differentiated instruction',
  ogImage = 'https://levelupteaching.ai/icon.png',
  ogType = 'website',
  canonicalUrl = 'https://levelupteaching.ai/',
  noindex = false,
  structuredData
}) => {
  const fullTitle = title.includes('Level Up Teaching') ? title : `${title} | Level Up Teaching`;

  // PROTECTION: Automatically block indexing on non-production environments
  const isProduction = typeof window !== 'undefined' && (
    window.location.hostname === 'levelupteaching.ai' ||
    window.location.hostname === 'www.levelupteaching.ai'
  );
  
  // Block indexing if:
  // 1. Explicitly requested via noindex prop, OR
  // 2. Not running on production domain (localhost, dev, staging, preview)
  const shouldBlockIndexing = noindex || !isProduction;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {shouldBlockIndexing && <meta name="robots" content="noindex, nofollow" />}
      {!shouldBlockIndexing && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Level Up Teaching" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

/**
 * Predefined structured data schemas
 */

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Level Up Teaching",
  "url": "https://levelupteaching.ai",
  "logo": "https://levelupteaching.ai/icon.png",
  "description": "AI-powered lesson planning platform for teachers",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Support",
    "url": "https://levelupteaching.ai/contact"
  },
  "sameAs": []
};

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Level Up Teaching",
  "applicationCategory": "EducationalApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "priceValidUntil": "2025-12-31",
    "description": "First month free trial"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "127"
  },
  "description": "AI-powered lesson planning tool that helps teachers create standards-aligned content, differentiated instruction materials, and professional assessments in minutes.",
  "screenshot": "https://levelupteaching.ai/icon.png",
  "softwareVersion": "1.0",
  "applicationSubCategory": "Teaching Tools"
};

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const faqSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

